import type {
  BlockOutput,
  BulkOperationBlockData,
  BulkOperationProgress,
  BulkOperationStatus,
  BulkOperationTarget,
} from '../../types';
import { str, toNum } from './guards';
import type { StructuredBulkOperation } from './types';

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;

const nonNegative = (value: unknown): number => {
  const n = toNum(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const percentage = (value: unknown): number =>
  Math.min(100, Math.max(0, nonNegative(value)));

const normalizeStatus = (value: unknown): BulkOperationStatus => {
  switch (value) {
    case 'in_progress':
    case 'completed':
    case 'failed':
      return value;
    default:
      return 'pending';
  }
};

export const buildBulkOperationBlock = (
  data: StructuredBulkOperation,
  toolCallId?: string
): BlockOutput => {
  const rawProgress = asRecord(data.progress);
  const total = Math.max(
    nonNegative(data.total),
    nonNegative(rawProgress?.total)
  );
  const progress: BulkOperationProgress = {
    total,
    processed: nonNegative(rawProgress?.processed),
    success: nonNegative(rawProgress?.success),
    failed: nonNegative(rawProgress?.failed),
    skipped: nonNegative(rawProgress?.skipped),
    percentage: percentage(rawProgress?.percentage),
  };

  const rawTarget = asRecord(data.target);
  const targetId = toNum(rawTarget?.id);
  const target: BulkOperationTarget | undefined =
    rawTarget && Number.isInteger(targetId) && targetId > 0
      ? {
          type: str(rawTarget.type) || 'record',
          id: targetId,
          name: str(rawTarget.name),
          url: str(rawTarget.url),
        }
      : undefined;

  const blockData: BulkOperationBlockData = {
    activityId: toNum(data.activity_id),
    activity: str(data.activity) || 'bulk_operation',
    status: normalizeStatus(data.status),
    title: str(data.title) || 'Processing bulk operation',
    total,
    progress,
    target,
    toolCallId,
  };

  return { type: 'bulkOperation', data: blockData };
};
